import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { FilePlus2, Eye, ChevronLeft, Search } from 'lucide-react';
import { getAssignedCandidates } from '../../../services/admin/api';
import './AssignedCandidates.css';

const AssignedCandidates = () => {
  const { employeeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const employeeName = location.state?.employeeName || 'Unknown';

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [sortColumn, setSortColumn] = useState('assignedAt');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getAssignedCandidates(employeeId);
        const list = Array.isArray(data) ? data : data?.data || [];
        setCandidates(list);
      } catch {
        setError('Failed to load assigned candidates.');
        setCandidates([]);
      } finally {
        setLoading(false);
      }
    };

    if (employeeId) fetchCandidates();
  }, [employeeId]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('desc');
    }
  };

  const filteredCandidates = useMemo(() => {
    return [...candidates]
      .filter((c) =>
        c.candidateId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone?.toString().includes(searchTerm) ||
        c.candidateStatus?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        if (!sortColumn) return 0;

        const valA = a[sortColumn];
        const valB = b[sortColumn];

        if (valA == null) return 1;
        if (valB == null) return -1;

        // Date sort
        if (sortColumn === 'assignedAt') {
          const dateA = new Date(valA).getTime();
          const dateB = new Date(valB).getTime();
          return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
        }

        // Number sort
        const numA = Number(valA);
        const numB = Number(valB);
        if (!isNaN(numA) && !isNaN(numB)) {
          return sortOrder === 'asc' ? numA - numB : numB - numA;
        }

        return sortOrder === 'asc'
          ? String(valA).toLowerCase().localeCompare(String(valB).toLowerCase())
          : String(valB).toLowerCase().localeCompare(String(valA).toLowerCase());
      });
  }, [candidates, searchTerm, sortColumn, sortOrder]);

  const startIndex = (currentPage - 1) * recordsPerPage;
  const paginatedCandidates = filteredCandidates.slice(
    startIndex,
    startIndex + recordsPerPage
  );

  const totalPages = Math.ceil(filteredCandidates.length / recordsPerPage);

  const formatStatusClass = (status) =>
    status ? status.toLowerCase().replace(/_/g, '-') : '';

  return (
    <div className="assigned-candidates-container">

      <div className="assigned-header">
        <button onClick={() => navigate(-1)} className="back-button">
          <ChevronLeft size={18} /> Back
        </button>
      </div>

      <div className="assigned-candidate-table-controls">
        <div className="assigned-candidate-entries-select">
          Show
          <select
            value={recordsPerPage}
            onChange={(e) => {
              setRecordsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          entries
        </div>

        <div className="assigned-candidate-search-wrapper">
          <input
            type="text"
            placeholder="Search by Candidate Name..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          <Search className="assigned-candidate-search-icon" size={18} />
        </div>
      </div>

      <div className="table-container">
        <table className="assigned-candidates-table">
          <thead>
            <tr>
              {[
                ['candidateId', 'Candidate ID'],
                ['fullName', 'Candidate Name'],
                ['clientName', 'Client Name'],
                ['email', 'Email'],
                ['phone', 'Phone'],
                ['assignedAt', 'Assigned At'],
                ['candidateStatus', 'Status'],
              ].map(([key, label]) => (
                <th key={key} onClick={() => handleSort(key)}>
                  {label} {sortColumn === key ? (sortOrder === 'asc' ? '↑' : '↓') : '↑↓'}
                </th>
              ))}
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="no-data loading-text">
                  Loading assigned candidates...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="8" className="no-data">{error}</td>
              </tr>
            ) : paginatedCandidates.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  No candidates assigned to this employee.
                </td>
              </tr>
            ) : (
              paginatedCandidates.map((cand) => (
                <tr key={cand.id}>
                  <td>{cand.candidateId}</td>
                  <td>{cand.fullName}</td>
                  <td>{cand.clientName}</td>
                  <td>{cand.email}</td>
                  <td>{cand.phone}</td>
                  <td>{new Date(cand.assignedAt).toLocaleString()}</td>
                  <td className={`status-cell ${formatStatusClass(cand.candidateStatus)}`}>
                    {cand.candidateStatus}
                  </td>
                  <td className="action-icons">
                    <button
                      className={`icon-btn ${cand.candidateStatus !== 'VERIFIED' ? 'disabled' : ''}`}
                      disabled={cand.candidateStatus !== 'VERIFIED'}
                      title="View Candidate Report"
                      onClick={() =>
                        navigate(`/admin/reports/${cand.candidateId}`, {
                          state: { heading: 'Candidate Report', candidate: cand },
                        })
                      }
                    >
                      <FilePlus2 size={18} />
                    </button>

                    <button
                      className="icon-btn"
                      title="View Candidate Details"
                      onClick={() =>
                        navigate(`/admin/candidate-details/${cand.candidateId}`, {
                          state: { candidate: cand },
                        })
                      }
                    >
                      <Eye size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="assigned-candidate-pagination">
        <div className="assigned-candidate-pagination-summary">
          Showing {(currentPage - 1) * recordsPerPage + 1}
          {' '}to{' '}
          {Math.min(currentPage * recordsPerPage, filteredCandidates.length)}
          {' '}of {filteredCandidates.length} entries
        </div>

     <div className="assigned-candidate-pagination-buttons">
  {/* Previous */}
  <button
    className="assigned-candidate-page-btn"
    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
    disabled={currentPage === 1}
  >
    Previous
  </button>

  {/* Page Numbers */}
  {Array.from({ length: totalPages }, (_, index) => {
    const pageNumber = index + 1;
    return (
      <button
        key={pageNumber}
        className={`assigned-candidate-page-btn ${
          currentPage === pageNumber ? "active" : ""
        }`}
        onClick={() => setCurrentPage(pageNumber)}
      >
        {pageNumber}
      </button>
    );
  })}

  {/* Next */}
  <button
    className="assigned-candidate-page-btn"
    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
    disabled={currentPage === totalPages || totalPages === 0}
  >
    Next
  </button>
</div>
      </div>
    </div>
  );
};

export default AssignedCandidates;
